import React, { useState, useEffect } from 'react'
import { Box, Typography, Button, IconButton, Modal, Card, CardContent, Tooltip } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate } from 'react-router-dom'
import MainButton from '../../../components/MainButton'
import PDFWebViewComponent from '../../../components/PDFWebViewComponent'
import { ContinueButton, BackIconButton } from '../../../components/Buttons'
import { useDocumentCreation } from '../../../contexts/DocumentCreationContext'
import SourceSelectionModal from './SourceSelectionModal'

// Document Viewer Modal Component
const DocumentViewerModal = ({ open, onClose, fileUrl, fileName }) => {
    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="document-viewer-modal"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    width: '90vw',
                    height: '90vh',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    position: 'relative',
                    boxShadow: 24,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderBottom: '1px solid #E5E7EB',
                        backgroundColor: '#F9FAFB',
                    }}
                >
                    <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                        {fileName || 'Document Viewer'}
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            padding: '4px',
                            '&:hover': { backgroundColor: '#E5E7EB' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: '20px' }} />
                    </IconButton>
                </Box>

                {/* Document Viewer */}
                <Box sx={{ width: '100%', height: 'calc(100% - 73px)' }}>
                    {fileUrl && (
                        <PDFWebViewComponent
                            initialFile={fileUrl}
                            hasLink={false}
                        />
                    )}
                </Box>
            </Box>
        </Modal>
    )
}

export default function UploadSourcesStepComponent({
    onNextClick,
    onBackClick,
    onFileSelect,
    selectedFiles,
    removeFile,
}) {
    const { document, updateDocument } = useDocumentCreation()
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)

    useEffect(() => {
        updateDocument({ referenceFiles: selectedFiles })
        console.log('document', document)
    }, [selectedFiles])

    const handleViewFile = (file) => {
        setSelectedDocument(file)
        setDocumentViewerOpen(true)
    }

    const handleCloseViewer = () => {
        setDocumentViewerOpen(false)
        setSelectedDocument(null)
    }

    const formatFileName = (fileName) => {
        if (!fileName) return ''
        return fileName.split('/').pop() || fileName
    }

    const handleContinue = () => {
        if (selectedFiles.length > 0) {
            onNextClick()
        }
    }

    const handleDeleteFile = (file) => {
        removeFile(file.name, true)
    }

    useEffect(() => {
        console.log('document', document.templateType.template_name)
    }, [document])

    const isFormValid = selectedFiles.length > 0 || document.templateType.template_name === "Seasonal mRNA BSR Template"
    
    return (
        <Box sx={{ 
            width: '100%', 
            height: '100%', 
            padding: '40px',
            paddingTop: '120px', // Account for fixed stepper
            '@media (max-width: 768px)': {
                paddingTop: '100px',
            },
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Box sx={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 999,
                width: 'clamp(600px, 60vw, 900px)',
                '@media (max-width: 768px)': {
                    width: 'calc(100vw - 32px)',
                    left: '16px',
                    transform: 'none',
                },
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 180px)',
                scrollbarWidth: 'none', // Firefox
                msOverflowStyle: 'none', // IE and Edge
                '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Opera
                },
            }}>
                <Box sx={{ 
                    position: 'sticky', 
                    top: 0,
                    backgroundColor: 'white', 
                    zIndex: 999, 
                    background: '#F9FAFB' 
                }}>
                    <Box sx={{ mb: '24px' }}>
                        <BackIconButton onClick={onBackClick} />
                        <Box>
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontSize: '24px',
                                    fontWeight: 600,
                                    color: '#111827',
                                    mb: 1
                                }}
                            >
                                Upload Source Files
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                <Typography 
                                    sx={{ 
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        color: '#374151',
                                    }}
                                >
                                    Data should be uploaded separately
                                </Typography>
                                <Tooltip title="For example, make sure you don’t combine your TFLs and SAP into one file. They should be uploaded as two separate files.">
                                    <InfoIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                
                
                    <Card sx={{
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                        boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                        border: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '24px',
                        width: '100%',
                        mb: 3,
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
                                {/* File Upload Area */}
                                <Box>
                                    <Box
                                        onClick={onFileSelect}
                                        sx={{
                                            border: '2px dashed #E5E7EB',
                                            borderRadius: '8px',
                                            padding: '40px',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: '#D1D5DB',
                                                backgroundColor: '#F9FAFB',
                                            },
                                        }}
                                    >
                                        <UploadFileIcon sx={{ fontSize: 48, color: '#9CA3AF', mb: 2 }} />
                                        <Typography sx={{ fontSize: '14px', color: '#374151', fontWeight: 500, mb: 1 }}>
                                            Click to upload source files
                                        </Typography>
                                        <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>
                                            PDF, DOC, DOCX up to 10MB
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
                { selectedFiles.length > 0 && (
                    <Box sx={{ 
                        flex: 1, 
                        overflowY: 'auto',
                        mb: 4,
                        minWidth: '100%'
                    }}>
                        <Card sx={{
                            backgroundColor: '#ffffff',
                            borderRadius: '8px',
                            boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                            border: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '24px',
                            width: '100%',
                            mb: '184px',
                        }}>
                            <CardContent
                                sx={{
                                    '& .MuiCardContent-root': {
                                        padding: '16px',
                                        '&:last-child': {
                                            paddingBottom: '0px !important',
                                        },
                                    },
                                }}
                            >       
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Box>
                                        <Box>
                                            {selectedFiles.map((file, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        padding: '12px 16px',
                                                        backgroundColor: '#F9FAFB',
                                                        borderRadius: '8px',
                                                        border: '1px solid #E5E7EB',
                                                        mb: 1,
                                                    }}
                                                >
                                                    <InsertDriveFileIcon sx={{ fontSize: 20, color: '#6B7280' }} />
                                                    <Typography sx={{ fontSize: '14px', color: '#374151', flex: 1 }}>
                                                        {formatFileName(file.name || file)}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        {/* <Button
                                                            onClick={() => handleViewFile(file)}
                                                            sx={{
                                                                px: 2,
                                                                py: 0.5,
                                                                backgroundColor: 'transparent',
                                                                color: '#4F46E5',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                minWidth: 'auto',
                                                                '&:hover': {
                                                                    backgroundColor: '#F3F4F6',
                                                                },
                                                            }}
                                                        >
                                                            View
                                                        </Button> */}
                                                        <Button
                                                            onClick={onFileSelect}
                                                            sx={{
                                                                px: 2,
                                                                py: 0.5,
                                                                backgroundColor: 'transparent',
                                                                color: '#4F46E5',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                minWidth: 'auto',
                                                                '&:hover': {
                                                                    backgroundColor: '#F3F4F6',
                                                                },
                                                            }}
                                                        >
                                                            Replace
                                                        </Button>
                                                        <Button
                                                            onClick={() => handleDeleteFile(file)}
                                                            sx={{
                                                                px: 2,
                                                                py: 0.5,
                                                                backgroundColor: '#DC2626',
                                                                color: '#fff',
                                                                borderRadius: '4px',
                                                                fontSize: '14px',
                                                                fontWeight: 500,
                                                                textTransform: 'none',
                                                                minWidth: 'auto',
                                                                '&:hover': {
                                                                    backgroundColor: '#b00020',
                                                                },
                                                            }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
            {/* Document Viewer Modal */}
            <DocumentViewerModal
                open={documentViewerOpen}
                onClose={handleCloseViewer}
                fileUrl={selectedDocument?.url || selectedDocument?.name}
                fileName={formatFileName(selectedDocument?.name || selectedDocument)}
            />

            {/* Fixed Continue Button */}
            <ContinueButton
                onClick={handleContinue}
                disabled={!isFormValid}
                text="Continue"
            />
        </Box>
    )
}